import {
  fetchBriefing,
  fetchBriefingDetail,
  fetchCrewMembers,
  fetchCrewTrackingConsent,
  groupByCrewIdentity,
  type CrewTrackingConsentRow,
} from "@produce/shared";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

/**
 * The reads behind the producer portal's production pages, in one place.
 *
 * Each page used to declare its `useQuery({ queryKey, queryFn })` inline. That
 * made the data unreachable from anywhere else, so nothing could warm a page
 * before the producer arrived on it — the page mounted, fired its queries, and
 * popped in as they landed. Defining them here lets the page and the route
 * prefetcher run *the same* query under *the same* key, which is what makes a
 * warm cache a cache hit instead of a second fetch.
 *
 * It also removes a live hazard: `["all-crew-members", id]` was declared twice
 * with different `select`s, so whichever page loaded first decided what the
 * other one got back.
 *
 * Shape: React Query "query options" objects. Pages spread them
 * (`useQuery({ ...allCrewMembersQuery(id), enabled: !!id })`); the prefetcher
 * hands them straight to `prefetchQuery`.
 */

/* -------------------------------------------------------------------------
 * Briefing / production record
 * ---------------------------------------------------------------------- */

/**
 * The production row.
 *
 * One definition, one cache key. The dashboard used to read this row under
 * ["production", id] and the schedule page under ["briefing", id] — the same
 * select, the same row, fetched twice per production and invalidated by only
 * one of the two names. Scope is enforced by RLS (owner, co-producer, admin).
 */
export function briefingQuery(productionId: string | undefined) {
  return {
    queryKey: ["briefing", productionId] as const,
    queryFn: () => fetchBriefing(supabase, productionId!),
  };
}

/**
 * Every production this producer can open — the page-header dropdown.
 *
 * The column list is the union of what the live-tracking page and the
 * analytics page each used to select under this very same key. They differed
 * (`navigation_address` vs `doors_open`/`doors_close`), so whichever page
 * mounted first decided what the other one read for the next five minutes —
 * analytics silently fell back to a default doors window whenever live
 * tracking had been visited first.
 *
 * `timezone` must stay selected: both consumers compute their day windows in
 * it (`currentBriefing.timezone || BRIEFING_TZ_DEFAULT`). It went unselected
 * for months while the code read it through a cast, so every non-Amsterdam
 * festival's analytics silently computed in Amsterdam time.
 */
export function producerBriefingsQuery(userId: string | undefined) {
  return {
    queryKey: ["producer-briefings", userId] as const,
    queryFn: async () => {
      if (!userId) return [];

      // RLS returns the user's own briefings (owner) or the one they co-produce.
      const { data, error } = await supabase
        .from("briefings")
        // prettier-ignore — one literal: Supabase infers the row type from it.
        .select(
          "id, festival_name, festival_date_start, festival_date_end, navigation_address, doors_open, doors_close, stages, timezone",
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  };
}

/**
 * Briefing + producer profile for the portal's read-only briefing page, in the
 * exact shape the crew app's briefing tab reads.
 *
 * optionalProducer: a co-producer is RLS-blocked from the owner's profile, so
 * the producer fetch must not fail the whole page — the briefing still loads,
 * just without the producer card.
 */
export function briefingViewQuery(productionId: string | undefined) {
  return {
    queryKey: ["briefing-view", productionId] as const,
    queryFn: () => fetchBriefingDetail(supabase, productionId!, { optionalProducer: true }),
  };
}

/** Per-day doors overrides for a multi-day production. */
export function briefingDayDoorsQuery(productionId: string | undefined) {
  return {
    queryKey: ["briefing_day_doors", productionId] as const,
    queryFn: async () => {
      if (!productionId) return [];
      const { data, error } = await supabase
        .from("briefing_day_doors")
        .select("event_date, doors_open, doors_close")
        .eq("briefing_id", productionId);
      if (error) throw error;
      return data ?? [];
    },
  };
}

/** Canonical per-day stages. */
export function briefingStagesQuery(productionId: string | undefined) {
  return {
    queryKey: ["briefing_stages", productionId] as const,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefing_stages")
        .select("*")
        .eq("briefing_id", productionId!)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  };
}

/* -------------------------------------------------------------------------
 * Crew
 * ---------------------------------------------------------------------- */

export type CrewMemberRow = Tables<"crew_members">;

/**
 * Every crew row for the production, by name.
 *
 * One definition for what used to be four byte-identical reads under four
 * cache keys (["all-crew-members"], ["crew-members"], ["crew-members-raw"],
 * ["crew-members-briefing"]) — three of which fired at once on the production
 * overview, pulling the whole crew table three times. Callers that need a
 * different order or shape derive it with React Query `select` rather than
 * re-querying.
 *
 * ["crew-members", id] is the surviving key because it is the one existing
 * mutations already invalidate (ManageCrew's invite flow, CrewHome). Every
 * reader of that key must go through here — a second queryFn on the same key
 * would silently decide what the others get back (see the note above
 * producerBriefingsQuery for what that costs).
 */
export function crewMembersQuery(productionId: string | undefined) {
  return {
    queryKey: ["crew-members", productionId] as const,
    queryFn: (): Promise<CrewMemberRow[]> => fetchCrewMembers(supabase, productionId!),
  };
}

/**
 * Compare two possibly-missing values the way Postgres ASC does: a missing
 * value sorts LAST, not first. Sorting client-side with `?? ""` would put
 * every crew member who has no arrival time yet at the TOP of the list.
 */
function compareNullsLast(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

/** Crew rows sorted the way the briefing page's arrival-time table shows them. */
export function byArrival(rows: CrewMemberRow[]): CrewMemberRow[] {
  return [...rows].sort(
    (a, b) =>
      compareNullsLast(a.event_date, b.event_date) ||
      compareNullsLast(a.arrival_time, b.arrival_time),
  );
}

/** Crew rostered for one festival day. */
export function crewMembersForDayQuery(productionId: string | undefined, eventDate: string) {
  return {
    queryKey: ["crew-members", productionId, eventDate] as const,
    queryFn: async () => {
      if (!productionId || !eventDate) return [];

      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("briefing_id", productionId)
        .eq("event_date", eventDate);

      if (error) throw error;
      return data;
    },
  };
}

/** How many crew the production overview's widget shows. */
const CREW_WIDGET_SIZE = 5;

/** Chronological order within one crew member's assignment list. */
function byStartTime(
  a: { event_date?: string | null; filming_start?: string | null },
  b: { event_date?: string | null; filming_start?: string | null },
): number {
  return (
    compareNullsLast(a.event_date, b.event_date) ||
    compareNullsLast(a.filming_start, b.filming_start)
  );
}

/**
 * The production dashboard's crew widget: the first five crew, each with all
 * their active assignments (the "Next up" decision is made client-side).
 *
 * It used to issue one assignments request PER crew member once the crew list
 * came back, so the widget's cost grew with the crew size and every one of
 * those had to land before anything rendered. Now: one crew read, dedupe to
 * the five people actually shown, then one assignments read scoped to those
 * five. A briefing-wide assignments read would be thousands of rows to render
 * five list items.
 *
 * Identity comes from the shared crewIdentityKey (briefing_crew_id → user_id →
 * lowercased email), so this widget answers "same person?" the same way the
 * crew list and the drag-drop paths do.
 */
export function crewWithAssignmentsQuery(productionId: string | undefined) {
  return {
    queryKey: ["crew-with-assignments", productionId] as const,
    queryFn: async () => {
      const crew: CrewMemberRow[] = await fetchCrewMembers(supabase, productionId!);

      // A person holds one crew_members row per festival day; the widget
      // shows them as one row with every day's assignments.
      const people = groupByCrewIdentity(crew).slice(0, CREW_WIDGET_SIZE);
      const rowIds = people.flatMap((person) => person.rows.map((row) => row.id));
      if (rowIds.length === 0) return [];

      const { data: assignments, error } = await supabase
        .from("crew_assignments")
        .select("*")
        .eq("briefing_id", productionId!)
        .eq("is_active", true)
        .in("crew_member_id", rowIds);

      if (error) throw error;

      const byCrewMemberId = new Map<string, typeof assignments>();
      for (const assignment of assignments ?? []) {
        const id = assignment.crew_member_id ?? "";
        byCrewMemberId.set(id, [...(byCrewMemberId.get(id) ?? []), assignment]);
      }

      return people.map((person) => ({
        ...person.rows[0],
        assignments: person.rows
          .flatMap((row) => byCrewMemberId.get(row.id) ?? [])
          .sort(byStartTime),
      }));
    },
  };
}

/* -------------------------------------------------------------------------
 * Lineup & assignments
 * ---------------------------------------------------------------------- */

/** The production's full lineup, in running order. */
export function lineupQuery(productionId: string | undefined) {
  return {
    queryKey: ["lineup", productionId] as const,
    queryFn: async () => {
      if (!productionId) return [];

      const { data, error } = await supabase
        .from("lineup")
        .select("*")
        .eq("briefing_id", productionId)
        .order("event_date", { ascending: true })
        .order("set_start", { ascending: true });

      if (error) throw error;
      return data;
    },
  };
}

/** Active crew assignments for one festival day. */
export function assignmentsForDayQuery(productionId: string | undefined, eventDate: string) {
  return {
    queryKey: ["assignments", productionId, eventDate] as const,
    queryFn: async () => {
      if (!productionId || !eventDate) return [];

      const { data, error } = await supabase
        .from("crew_assignments")
        .select("*")
        .eq("briefing_id", productionId)
        .eq("event_date", eventDate)
        .eq("is_active", true);

      if (error) throw error;
      return (data ?? []).map((d) => ({
        ...d,
        crew_member_id: d.crew_member_id ?? "",
        artist_name: d.artist_name ?? undefined,
        assignment_type: d.assignment_type as "artist" | "custom",
        subject: d.subject ?? undefined,
        notes: d.notes ?? undefined,
      }));
    },
  };
}

/** How many active assignments the production has. */
export function assignmentCountQuery(productionId: string | undefined) {
  return {
    queryKey: ["assignment-count", productionId] as const,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("crew_assignments")
        .select("*", { count: "exact", head: true })
        .eq("briefing_id", productionId!)
        .eq("is_active", true);

      if (error) throw error;
      return count || 0;
    },
  };
}

/** How many lineup slots the production has. */
export function lineupCountQuery(productionId: string | undefined) {
  return {
    queryKey: ["lineup-count", productionId] as const,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("lineup")
        .select("*", { count: "exact", head: true })
        .eq("briefing_id", productionId!);

      if (error) throw error;
      return count || 0;
    },
  };
}

/* -------------------------------------------------------------------------
 * Live tracking
 * ---------------------------------------------------------------------- */

export type { CrewTrackingConsentRow } from "@produce/shared";

/**
 * Who on this production is sharing their location, and who switched it off.
 *
 * Goes through the get_crew_tracking_consent RPC rather than reading
 * crew_settings directly. Note that a producer CAN read that table — the
 * policy "Producers can view crew settings for their productions"
 * (20251219101732) has been there since December, and Analytics reads it that
 * way. The RPC earns its place for three other reasons: it also answers for
 * active co-producers, whom that policy does not cover; it returns the two
 * consent booleans and nothing else, instead of every column of someone's
 * settings; and it answers for the whole production in one call. Without it
 * the crew list cannot tell "switched off" from "phone is dead" — see
 * crewTrackingStatus. The read itself is shared with the producer app
 * (fetchCrewTrackingConsent in @produce/shared).
 */
export function crewTrackingConsentQuery(productionId: string | undefined) {
  return {
    queryKey: ["crew-tracking-consent", productionId] as const,
    queryFn: async (): Promise<CrewTrackingConsentRow[]> => {
      if (!productionId) return [];
      return fetchCrewTrackingConsent(supabase, productionId);
    },
  };
}

/** Everyone rostered for one day, GPS or not — the tracking map's crew list. */
export function allCrewMembersForDayQuery(productionId: string | undefined, eventDate: string) {
  return {
    queryKey: ["all-crew-members", productionId, eventDate] as const,
    queryFn: async () => {
      if (!productionId || !eventDate) return [];

      const { data, error } = await supabase
        .from("crew_members")
        .select("id, user_id, name, function, color")
        .eq("briefing_id", productionId)
        .eq("event_date", eventDate);

      if (error) throw error;
      return data || [];
    },
  };
}

/** Map style / zoom preferences for the tracking map. Absent is not an error. */
export function mapSettingsQuery(productionId: string | undefined) {
  return {
    queryKey: ["map-settings", productionId] as const,
    queryFn: async () => {
      if (!productionId) return null;

      const { data, error } = await supabase
        .from("map_settings")
        .select("*")
        .eq("briefing_id", productionId)
        .single();

      // PGRST116 = no row: this production simply has no saved map settings,
      // which is a normal state. Anything else is a failure and must not be
      // cached as "no settings".
      if (error) {
        if (error.code === "PGRST116") return null;
        throw error;
      }
      return data;
    },
  };
}

/**
 * Zones the read-only tracking map may draw.
 *
 * Private cache namespace ("producer-tracking") so this map never inherits
 * another page's superset — Analytics fills `["festival-areas", id]` via
 * `select("*")` (festival_boundary included), which used to leak the boundary
 * onto this map.
 */
export function trackingFestivalAreasQuery(productionId: string | undefined) {
  return {
    queryKey: ["festival-areas", "producer-tracking", productionId] as const,
    queryFn: async () => {
      if (!productionId) return [];

      // Fetch ALL stage zones (the caller filters client-side for the day
      // fallback). Embed briefing_stages.label so the polygon label renders
      // from the canonical FK source rather than a denorm cache that can drift
      // across page navigations.
      const { data: stageZones, error: stageError } = await supabase
        .from("festival_areas")
        .select("*, briefing_stages(label)")
        .eq("briefing_id", productionId)
        .eq("area_type", "stage_zone");

      // Location zones (media office, crew catering) are not day-specific.
      const { data: locationZones, error: locationError } = await supabase
        .from("festival_areas")
        .select("*")
        .eq("briefing_id", productionId)
        .in("area_type", ["media_office", "crew_catering"]);

      // Throw, don't return []. A backend failure cached as a successful
      // empty result draws a map with no zones and no error — indistinguishable
      // from a production that genuinely has none, for the whole staleTime.
      if (stageError) throw stageError;
      if (locationError) throw locationError;

      return [...(stageZones ?? []), ...(locationZones ?? [])];
    },
  };
}
